// MobileCoreMenu.js — Enhanced Solana-style glassmorphic mobile menu with wallet integration
import React, { useState, useEffect, useCallback, useMemo, useRef, lazy, Suspense } from "react";
import { apiFetch } from '../utils/serverConfig';
import './MobileCoreMenu.css';

// Debug logging for mobile issues
console.log('📱 MobileCoreMenu: Module loading...');

// Lazy-load promo raffles at module scope so chunk is registered once
const MatrixPromo = lazy(() => import('./matrixpromo'));
const Christmas = lazy(() => import('./Christmas'));

const menuItems = [
  { key: "kiosk", label: "Kiosk Mode", icon: "🏪", className: "kiosk-mode", description: "Public Check-in", component: "KioskMode" },
  { key: "admin", label: "Admin Mode", icon: "⚙️", className: "admin-mode", description: "Control Center", component: "AdminMode" },
  { key: "showmode", label: "Show Mode", icon: "🎟️", className: "show-mode", description: "Event / Showcase control (Admin PIN)", component: "ShowMode" },
  { key: "waiver", label: "Waiver Form", icon: "✍️", className: "waiver-mode", description: "Digital waiver & trials", component: "WaiverForm" },
  { key: "promo-roulette", label: "Promo Roulette", icon: "🎡", className: "promo-roulette", description: "Spin promos & prizes", component: "PromoRoulette" },
  { key: "blackfriday", label: "Matrix Rashguard", icon: "🎟️", className: "blackfriday", description: "Limited merch drop & NFT collectible", component: "MatrixPromo" },
  { key: "christmas", label: "Christmas", icon: "🎄", className: "christmas", description: "Holiday raffle & gift box", component: "Christmas" },
  { key: "leads", label: "Leads Manager", icon: "📇", className: "leads-mode", description: "Signed leads & contact database", component: "LeadsManager" },
  { key: "tavern", label: "Student Tavern", icon: "🏆", className: "student-mode", description: "Community & learning chat", component: "Tavern" },
  { key: "studentportal", label: "Student Portal", icon: "🎓", className: "student-portal", description: "Portal: profile, belts, awards & messaging", component: "StudentPortal" },
  { key: "instructor", label: "Instructor", icon: "🥋", className: "instructor-mode", description: "Instructor Chat", component: "InstructorChat" },
  { key: "store", label: "Store", icon: "🛒", className: "store-mode", description: "Buy gear & memberships", component: "StoreOrder" },
];

// Admin wallet addresses (from env var REACT_APP_ADMIN_WALLETS, comma-separated)
// Be defensive when reading env vars so we don't call .split on undefined.
const ADMIN_WALLETS = (typeof process !== 'undefined' && process.env.REACT_APP_ADMIN_WALLETS)
  ? process.env.REACT_APP_ADMIN_WALLETS.split(',').map(s => s.trim().toLowerCase()).filter(Boolean)
  : [
      '0x8bfc802e8decc49b63158b9ec4122e04a22d819c', // Example admin wallet
    ];

export default function MobileCoreMenu({ 
  onSelect, 
  walletAddress, 
  isAdmin: propIsAdmin, 
  onConnectWallet, 
  currentView,
  onDataSync,
  onNotification,
  adminActions,
  realTimeData 
}) {
  const [open, setOpen] = useState(false);
  const [animating, setAnimating] = useState(false);
  const [walletConnected, setWalletConnected] = useState(false);
  const [walletAddressState, setWalletAddressState] = useState(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [showWalletModal, setShowWalletModal] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [showConnectPopup, setShowConnectPopup] = useState(false);
  const [autoConnectAttempted, setAutoConnectAttempted] = useState(false);
  const [showHorizontalMenu, setShowHorizontalMenu] = useState(false);
  const [dataSync, setDataSync] = useState({ lastSync: null, status: 'idle' });
  const [notifications, setNotifications] = useState([]);
  const [quickActions, setQuickActions] = useState({
    messageCount: 0,
    syncPending: false,
    systemAlerts: 0
  });
  const orbRef = useRef(null);
  const [fallbackVisible, setFallbackVisible] = useState(false);
  const [fallbackStats, setFallbackStats] = useState({ students: 0, leads: 0, checkedToday: 0, revenue: 0, lastUpdated: null });
  const [fallbackMinimized, setFallbackMinimized] = useState(false);
  // Promo modal state (Black Friday raffle)
  const [showPromo, setShowPromo] = useState(false);
  const [promoType, setPromoType] = useState(null);

  // Fetch stats for fallback admin panel
  const fetchFallbackStats = useCallback(async (force = false) => {
    try {
      const res = await apiFetch('/api/data');
      const data = await res.json();

      // students count
      let studentsCount = 0;
      if (Array.isArray(data.students)) studentsCount = data.students.length;
      else if (data.students && typeof data.students === 'object') studentsCount = Object.keys(data.students).length;

      // leads count (try common keys)
      let leadsCount = 0;
      if (Array.isArray(data.leads)) leadsCount = data.leads.length;
      else if (Array.isArray(data.waiver_leads)) leadsCount = data.waiver_leads.length;
      else if (Array.isArray(data.students)) leadsCount = data.students.filter(s => s && (s.isLead || s.lead)).length || 0;

      // checked today — flexible detection
      let checkedToday = 0;
      const todayISO = new Date().toISOString().slice(0,10);
      const att = data.attendance;
      if (!att) checkedToday = 0;
      else if (Array.isArray(att)) {
        checkedToday = att.filter(a => {
          const d = a.date || a.datetime || a.checked_at || a.timestamp || a.time;
          if (!d) return false;
          return String(d).startsWith(todayISO);
        }).length;
      } else if (typeof att === 'object') {
        if (Array.isArray(att[todayISO])) {
          checkedToday = att[todayISO].length;
        } else {
          checkedToday = Object.values(att).reduce((sum, v) => {
            if (!v) return sum;
            if (Array.isArray(v)) {
              return sum + v.filter(a => {
                const d = a.date || a.datetime || a.checked_at || a.timestamp || a.time;
                return d && String(d).startsWith(todayISO);
              }).length;
            }
            return sum;
          }, 0);
        }
      }

      const revenue = studentsCount * 129;

      setFallbackStats({ students: studentsCount, leads: leadsCount, checkedToday, revenue, lastUpdated: new Date() });
    } catch (err) {
      console.error('fetchFallbackStats error', err);
      setFallbackStats(prev => ({ ...prev, lastUpdated: new Date() }));
    }
  }, []);

  // Small presentational component for the 4-stat grid.
  // Adds a small shimmer on first load and supports per-stat click to open admin tabs.
  const StatsGrid = ({ fallbackStats, realTimeData, onRefresh, onStatClick }) => {
    // Compute stable stats: prefer realtime values when present, but derive revenue
    // from students count if monthlyRevenue is not provided by the API.
    const studentsCount = (realTimeData?.students?.length ?? fallbackStats.students ?? 0);
    const leadsCount = (realTimeData?.leads?.length ?? fallbackStats.leads ?? 0);
    const checkedTodayCount = (realTimeData?.todayAttendance ?? fallbackStats.checkedToday ?? 0);
    // If API provides a monthlyRevenue explicitly use it; otherwise derive as students * 129
    const revenueAmount = (typeof realTimeData?.monthlyRevenue === 'number')
      ? realTimeData.monthlyRevenue
      : (studentsCount * 129) || (fallbackStats.revenue ?? 0);

    const stats = {
      students: studentsCount,
      leads: leadsCount,
      checkedToday: checkedTodayCount,
      revenue: revenueAmount
    };

    // Local refresh indicator
    const [refreshing, setRefreshing] = useState(false);

    // Consider the grid 'loading' if we don't have a lastUpdated timestamp and no realtime data
    const loading = !fallbackStats?.lastUpdated && !realTimeData;

    const handleRefresh = async () => {
      if (typeof onRefresh !== 'function') return;
      setRefreshing(true);
      try {
        await onRefresh(true);
      } catch (e) {
        console.error('StatsGrid refresh failed', e);
      } finally {
        setRefreshing(false);
      }
    };

    const statItems = [
      { key: 'students', label: 'Students', value: stats.students },
      { key: 'leads', label: 'Leads', value: stats.leads },
      { key: 'attendance', label: 'Checked Today', value: stats.checkedToday },
      { key: 'revenue', label: 'Monthly Rev', value: stats.revenue }
    ];

    return (
      <div style={{ marginTop: 10, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        {loading ? (
          // show four shimmer placeholders
          statItems.map((it, idx) => (
            <div key={idx} style={{ padding: 8 }}>
              <div className="stat-shimmer" style={{ width: '100%' }} />
            </div>
          ))
        ) : (
          // show interactive stat tiles
          statItems.map((it) => (
            <div
              key={it.key}
              role="button"
              aria-label={`${it.label} — ${it.value}`}
              tabIndex={0}
              onClick={() => typeof onStatClick === 'function' && onStatClick(it.key)}
              onKeyDown={(e) => {
                // Support keyboard activation for accessibility (Enter / Space)
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  e.stopPropagation();
                  if (typeof onStatClick === 'function') onStatClick(it.key);
                }
              }}
              style={{ padding: 8, borderRadius: 8, background: 'rgba(0,0,0,0.02)', textAlign: 'center', cursor: 'pointer' }}
            >
              <div style={{ fontSize: 18, fontWeight: it.key === 'revenue' ? 800 : 700, color: '#111' }}>
                {it.key === 'revenue' ? `$${Number(it.value || 0).toLocaleString()}` : String(it.value ?? 0)}
              </div>
              <div style={{ fontSize: 12, color: '#666' }}>{it.label}</div>
            </div>
          ))
        )}

        <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
          <button onClick={handleRefresh} style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid rgba(0,0,0,0.06)', background: '#fff', cursor: 'pointer' }}>
            {refreshing ? 'Refreshing...' : (loading ? 'Loading...' : 'Refresh')}
          </button>
          <div style={{ fontSize: 11, color: '#444' }}>
            <span style={{ color: '#0fffd9', textShadow: '0 0 8px rgba(20,241,149,0.6)' }}>Last:</span>
            {' '}
            {fallbackStats?.lastUpdated ? new Date(fallbackStats.lastUpdated).toLocaleTimeString() : 'n/a'}
          </div>
        </div>
      </div>
    );
  };

  // Use prop values if provided, otherwise use internal state
  const currentWalletAddress = walletAddress || walletAddressState;
  // Determine admin: prefer an explicit true prop, but allow internal state or persisted localStorage to enable admin features
  // (this avoids a parent-prop === false silently disabling admin when local persistence or wallet indicates admin)
  const persistedAdmin = (typeof window !== 'undefined' && localStorage.getItem('bragabjj_admin_status') === 'true');
  const currentIsAdmin = (propIsAdmin === true) || isAdmin || persistedAdmin;

  // Speak welcome admin when admin detected
  useEffect(() => {
    if (currentIsAdmin) {
      // Only speak if not already spoken in this session
      if (!window.__bragabjj_admin_welcome_spoken) {
        window.__bragabjj_admin_welcome_spoken = true;
        if ('speechSynthesis' in window) {
          const utter = new window.SpeechSynthesisUtterance('Welcome admin. Supreme access granted.');
          utter.rate = 1.05;
          utter.pitch = 1.1;
          utter.volume = 1;
          window.speechSynthesis.speak(utter);
        }
      }
    }
  }, [currentIsAdmin]);

  // Enhanced MetaMask detection
  const isMetaMaskAvailable = useCallback(() => {
    return typeof window !== 'undefined' && 
           typeof window.ethereum !== 'undefined' && 
           (window.ethereum?.isMetaMask || window.ethereum?.providers?.some(p => p.isMetaMask));
  }, []);

  // Note: wallet-browser heuristics moved to `walletBrowserInfo`; isInWalletBrowser unused and removed.

  // Enhanced wallet browser detection
  const walletBrowserInfo = useMemo(() => {
    if (!isMetaMaskAvailable()) return { inBrowser: false, name: null };
    
    if (window.ethereum?.isMetaMask) return { inBrowser: true, name: 'MetaMask' };
    if (window.ethereum?.isTrust) return { inBrowser: true, name: 'Trust Wallet' };
    if (window.ethereum?.isCoinbaseWallet) return { inBrowser: true, name: 'Coinbase Wallet' };
    
    // Check for multiple providers
    if (window.ethereum?.providers) {
      const metaMaskProvider = window.ethereum?.providers.find(p => p.isMetaMask);
      if (metaMaskProvider) return { inBrowser: true, name: 'MetaMask' };
    }
    
    return { inBrowser: false, name: null };
  }, [isMetaMaskAvailable]);

  // Check if wallet address is admin
  const checkAdminStatus = useCallback((address) => {
    if (!address) return false;
    return ADMIN_WALLETS.includes(address.toLowerCase());
  }, []);

  // Enhanced wallet connection with persistence
  const persistWalletState = useCallback((address, adminStatus) => {
    if (address) {
      localStorage.setItem('bragabjj_wallet_address', address);
      localStorage.setItem('bragabjj_admin_status', adminStatus.toString());
    } else {
      localStorage.removeItem('bragabjj_wallet_address');
      localStorage.removeItem('bragabjj_admin_status');
    }
  }, []);

  // Enhanced data synchronization management
  // Notification management (moved above data sync so helpers are defined before use)
  const removeNotification = useCallback((id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  const addNotification = useCallback((notification) => {
    setNotifications(prev => [...prev, notification]);

    if (notification.duration) {
      setTimeout(() => {
        removeNotification(notification.id);
      }, notification.duration);
    }

    // Call parent notification handler
    onNotification?.(notification);
  }, [onNotification, removeNotification]);

  const handleDataSync = useCallback(async (force = false) => {
    if (dataSync.status === 'syncing' && !force) return;

    setDataSync(prev => ({ ...prev, status: 'syncing' }));
    setQuickActions(prev => ({ ...prev, syncPending: true }));

    try {
      const syncResult = await onDataSync?.();
      setDataSync({
        lastSync: new Date(),
        status: 'success'
      });
      
      // Show success notification
      addNotification({
        id: Date.now(),
        type: 'success',
        title: 'Data Synchronized',
        message: 'All data is up to date',
        duration: 3000
      });

      return syncResult;
    } catch (error) {
      console.error('Data sync failed:', error);
      setDataSync(prev => ({ ...prev, status: 'error' }));
      
      // Show error notification
      addNotification({
        id: Date.now(),
        type: 'error',
        title: 'Sync Failed',
        message: 'Unable to synchronize data',
        duration: 5000
      });
    } finally {
      setQuickActions(prev => ({ ...prev, syncPending: false }));
    }
  }, [dataSync.status, onDataSync, addNotification]);

  

  // Enhanced admin quick actions
  const handleQuickAction = useCallback(async (action, data) => {
    if (!currentIsAdmin) return;

    try {
      switch (action) {
        case 'emergency_sync':
          await handleDataSync(true);
          break;
        case 'broadcast_message':
          if (adminActions?.sendBroadcast) {
            await adminActions.sendBroadcast(data);
            addNotification({
              id: Date.now(),
              type: 'success',
              title: 'Message Sent',
              message: `Broadcast sent to ${data.recipients} recipients`,
              duration: 4000
            });
          }
          break;
        case 'system_alert':
          setQuickActions(prev => ({ 
            ...prev, 
            systemAlerts: prev.systemAlerts + 1 
          }));
          addNotification({
            id: Date.now(),
            type: 'warning',
            title: 'System Alert',
            message: data.message,
            duration: 6000
          });
          break;
        default:
          console.log('Unknown quick action:', action);
      }
    } catch (error) {
      console.error('Quick action failed:', error);
      addNotification({
        id: Date.now(),
        type: 'error',
        title: 'Action Failed',
        message: `Unable to complete ${action}`,
        duration: 4000
      });
    }
  }, [currentIsAdmin, adminActions, handleDataSync, addNotification]);

  // Enhanced wallet connection check with better MetaMask detection
  const checkWalletConnection = useCallback(async () => {
    if (!isMetaMaskAvailable()) {
      console.log('MetaMask not available for connection check');
      setWalletConnected(false);
      if (!walletAddress) setWalletAddressState(null);
      if (propIsAdmin === undefined) setIsAdmin(false);
      persistWalletState(null, false);
      return;
    }

    try {
      console.log('Checking wallet connection...');
      const accounts = await window.ethereum.request({ method: 'eth_accounts' });
      console.log('Current accounts:', accounts);
      
      if (accounts && accounts.length > 0) {
        const address = accounts[0];
        console.log('Wallet is connected:', address);
        setWalletConnected(true);
        if (!walletAddress) setWalletAddressState(address);
        
        const adminStatus = checkAdminStatus(address);
        if (propIsAdmin === undefined) setIsAdmin(adminStatus);
        
        // Persist wallet state
        persistWalletState(address, adminStatus);
        
        // Show welcome message for admin
        if (adminStatus && !showWelcome) {
          setShowWelcome(true);
          setTimeout(() => setShowWelcome(false), 4000);
        }
      } else {
        console.log('No wallet accounts found');
        setWalletConnected(false);
        if (!walletAddress) setWalletAddressState(null);
        if (propIsAdmin === undefined) setIsAdmin(false);
        persistWalletState(null, false);
      }
    } catch (error) {
      console.error('Error checking wallet connection:', error);
      setWalletConnected(false);
      if (!walletAddress) setWalletAddressState(null);
      if (propIsAdmin === undefined) setIsAdmin(false);
      persistWalletState(null, false);
    }
  }, [isMetaMaskAvailable, checkAdminStatus, showWelcome, persistWalletState, walletAddress, propIsAdmin]);

  // Enhanced wallet initialization with better MetaMask detection
  const initializeWallet = useCallback(async () => {
    if (!isMetaMaskAvailable()) {
      console.log('MetaMask not available for initialization');
      return;
    }
    
    try {
      console.log('Initializing wallet...');
      
      // Check for cached wallet state
      const cachedWalletAddress = localStorage.getItem('bragabjj_wallet_address');
      const cachedAdminStatus = localStorage.getItem('bragabjj_admin_status') === 'true';
      
      if (cachedWalletAddress && !walletAddress) {
        console.log('Found cached wallet address:', cachedWalletAddress);
        
        // Verify the cached wallet is still connected
        const accounts = await window.ethereum.request({ method: 'eth_accounts' });
        if (accounts && accounts.includes(cachedWalletAddress)) {
          console.log('Cached wallet is still connected');
          setWalletAddressState(cachedWalletAddress);
          setWalletConnected(true);
          if (propIsAdmin === undefined) setIsAdmin(cachedAdminStatus);
        } else {
          console.log('Cached wallet is no longer connected, clearing cache');
          // Clear cache if wallet is no longer connected
          localStorage.removeItem('bragabjj_wallet_address');
          localStorage.removeItem('bragabjj_admin_status');
          localStorage.removeItem('bragabjj_wallet_confirmed');
          localStorage.removeItem('bragabjj_wallet_confirmed_time');
          setWalletConnected(false);
          setWalletAddressState(null);
          if (propIsAdmin === undefined) setIsAdmin(false);
        }
      }
    } catch (error) {
      console.error('Error initializing wallet:', error);
      // Clear any potentially corrupted cache
      localStorage.removeItem('bragabjj_wallet_address');
      localStorage.removeItem('bragabjj_admin_status');
      localStorage.removeItem('bragabjj_wallet_confirmed');
      localStorage.removeItem('bragabjj_wallet_confirmed_time');
    }
  }, [isMetaMaskAvailable, walletAddress, propIsAdmin]);

  // Enhanced auto-connect wallet on page load
  const autoConnectWallet = useCallback(async () => {
    if (autoConnectAttempted || !isMetaMaskAvailable()) {
      return;
    }
    
    setAutoConnectAttempted(true);
    console.log('Attempting auto-connect to wallet...');
    
    try {
      // Check if already connected
      const accounts = await window.ethereum.request({ method: 'eth_accounts' });
      if (accounts && accounts.length > 0) {
        console.log('Auto-connect successful, accounts found:', accounts);
        await checkWalletConnection();
      } else {
        console.log('Auto-connect: No connected accounts found');
      }
    } catch (error) {
      console.error('Auto-connect failed:', error);
    }
  }, [autoConnectAttempted, isMetaMaskAvailable, checkWalletConnection]);

  // Expose a global wallet modal opener so desktop can reuse the same modal logic
  useEffect(() => {
    const opener = async () => {
      return new Promise((resolve, reject) => {
        try {
          setShowWalletModal(true);
          // Poll for walletConnected state
          const t = setInterval(() => {
            if (walletConnected) {
              clearInterval(t);
              resolve(true);
            }
          }, 250);
          // Timeout after 30s
          setTimeout(() => {
            clearInterval(t);
            reject(new Error('Wallet modal timed out'));
          }, 30000);
        } catch (e) { reject(e); }
      });
    };
    try { window.openWalletModal = opener; } catch (e) { /* ignore */ }
    return () => { try { delete window.openWalletModal; } catch (e) {} };
  }, [walletConnected]);

  // Enhanced connect to wallet with better MetaMask detection
  const connectWallet = async () => {
    console.log('Starting wallet connection...');
    
    // Check if MetaMask is available
    if (!isMetaMaskAvailable()) {
      console.log('MetaMask not detected');
      
      // Enhanced MetaMask detection and installation with wallet browser redirect
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      if (isMobile) {
        // For mobile, create deep link to open this page in MetaMask browser
        const mmUrl = `https://metamask.app.link/dapp/${window.location.host}${window.location.pathname}`;
        
        // Show alert with instructions
        alert(
          'MetaMask wallet not detected!\n\n' +
          'To connect your wallet:\n' +
          '1. Install MetaMask mobile app\n' +
          '2. Open this page in MetaMask browser\n\n' +
          'Opening MetaMask app now...'
        );
        
        // Try to open in MetaMask browser
        window.open(mmUrl, '_blank');
      } else {
        // For desktop, direct to MetaMask extension
        alert(
          'MetaMask extension not detected!\n\n' +
          'Please install MetaMask browser extension to connect your wallet.\n\n' +
          'Opening MetaMask download page...'
        );
        
        window.open('https://metamask.io/download/', '_blank');
      }
      return;
    }

    // Use external connect function if provided
    if (onConnectWallet && typeof onConnectWallet === 'function') {
      try {
        console.log('Using external wallet connection...');
        await onConnectWallet();
        return;
      } catch (error) {
        console.error('External wallet connect failed, falling back to internal method:', error);
      }
    }

    setIsConnecting(true);
    try {
      console.log('Requesting MetaMask connection...');
      
      // Always use eth_requestAccounts to ensure proper connection
      const accounts = await window.ethereum.request({ 
        method: 'eth_requestAccounts' 
      });
      
      console.log('MetaMask responded with accounts:', accounts);
      
      if (accounts && accounts.length > 0) {
        const address = accounts[0];
        console.log('Wallet connected successfully:', address);
        
        // Update connection state immediately
        setWalletConnected(true);
        if (!walletAddress) setWalletAddressState(address);
        setShowWalletModal(false);
        
        const adminStatus = checkAdminStatus(address);
        if (propIsAdmin === undefined) setIsAdmin(adminStatus);
        
        // Persist wallet state
        persistWalletState(address, adminStatus);
        
        // Show connect popup for successful connections
        setShowConnectPopup(true);
        setTimeout(() => setShowConnectPopup(false), 6000);
        
        // Show welcome message for admin
        if (adminStatus) {
          setShowWelcome(true);
          setTimeout(() => setShowWelcome(false), 4000);
        }
        
        // Enhanced success feedback with haptic feedback on mobile
        try {
          if (navigator.vibrate) {
            navigator.vibrate([50, 100, 50]);
          }
        } catch (e) {
          // Vibration not supported
        }
        
        // Visual feedback on wallet connector
        const walletConnector = document.querySelector('.wallet-connector');
        if (walletConnector) {
          walletConnector.style.transform = 'scale(1.05)';
          walletConnector.style.borderColor = '#14f195';
          setTimeout(() => {
            walletConnector.style.transform = 'scale(1)';
            walletConnector.style.borderColor = '';
          }, 500);
        }
        
        // Additional confirmation in wallet browser if mobile
        const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
        if (isMobile && window.ethereum?.isMetaMask) {
          // Check if we're in MetaMask browser
          try {
            // Perform a signature request for additional confirmation
            const message = `Welcome to BragaBJJ! Confirming wallet connection.\n\nWallet: ${address}\nTime: ${new Date().toLocaleString()}`;
            
            await window.ethereum.request({
              method: 'personal_sign',
              params: [message, address],
            });
            
            console.log('Wallet connection confirmed with signature');
            
            // Store signature confirmation
            localStorage.setItem('bragabjj_wallet_confirmed', 'true');
            localStorage.setItem('bragabjj_wallet_confirmed_time', Date.now().toString());
            
          } catch (signError) {
            console.log('Signature confirmation skipped or cancelled:', signError);
            // Connection still valid even without signature
          }
        }
        
        console.log('Wallet connection process completed successfully');
      } else {
        console.error('No accounts returned from MetaMask');
        alert('No accounts found. Please ensure you have accounts in your MetaMask wallet.');
      }
    } catch (error) {
      console.error('Failed to connect wallet:', error);
      if (error.code === 4001) {
        // User rejected the request
        console.log('User rejected wallet connection');
        alert('Wallet connection was cancelled. Please try again and approve the connection in your MetaMask wallet.');
      } else if (error.code === -32002) {
        // Request already pending
        console.log('Wallet connection request already pending');
        alert('Please check your MetaMask wallet - there may be a pending connection request.');
      } else {
        console.error('Unexpected wallet connection error:', error);
        alert('Failed to connect wallet. Please ensure MetaMask is properly installed and try again.');
      }
    } finally {
      setIsConnecting(false);
    }
  };

  // Enhanced disconnect wallet with persistence cleanup
  const disconnectWallet = () => {
    setWalletConnected(false);
    if (!walletAddress) setWalletAddressState(null);
    if (propIsAdmin === undefined) setIsAdmin(false);
    setShowWalletModal(false);
    setShowWelcome(false);
    setShowConnectPopup(false);
    
    // Clear persisted state
    persistWalletState(null, false);
  };

  // Handle wallet connector click (stop propagation from parent overlays)
  const handleWalletConnectorClick = (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    if (walletConnected) {
      setShowWalletModal(true);
    } else {
      connectWallet();
    }
  };

  const handleOrbClick = () => {
    console.log('MobileCoreMenu: Orb clicked, current open state:', open);
    
    // If we're in admin mode and have mobile view, show horizontal menu instead
    try {
      if (currentIsAdmin && (typeof window !== 'undefined') && window.innerWidth <= 768) {
        setShowHorizontalMenu(!showHorizontalMenu);
        return;
      }
    } catch (e) {
      console.warn('📱 Error checking screen width:', e);
    }
    
    if (!open) {
      setOpen(true);
      setAnimating(true);
      
      // Enhanced visual feedback on orb click
      try {
        if (navigator.vibrate) {
          navigator.vibrate([50, 100, 50]);
        }
      } catch (e) {
        // Vibration not supported
      }
      
      // Remove animation class after animation completes
      setTimeout(() => setAnimating(false), 1000);
    } else {
      setOpen(false);
      setAnimating(false);
    }
  };

  const handleMenuSelect = useCallback((key) => {
    console.log('MobileCoreMenu: handleMenuSelect called with key:', key);
    
    // Add visual feedback before selection
    const selectedBtn = document.querySelector(`[data-key="${key}"]`);
    if (selectedBtn) {
      selectedBtn.style.transform = 'scale(0.95)';
      selectedBtn.style.background = 'rgba(20, 241, 149, 0.15)';
      
      // Enhanced haptic feedback
      try {
        if (navigator.vibrate) {
          navigator.vibrate([30, 50, 30]);
        }
      } catch (e) {
        // Vibration not supported
      }
      
        setTimeout(() => {
          selectedBtn.style.transform = '';
          selectedBtn.style.background = '';

          // Special-case: when user taps Waiver from the mobile orb,
          // route them into Kiosk Mode and instruct Kiosk to open the
          // Waiver modal. This prevents the Mobile menu from bypassing
          // the Kiosk flow while still delivering the Waiver.
          let routedKey = key;
          try {
            if (key === 'waiver') {
              // Persist a flag so KioskMode can open the Waiver modal when it mounts.
              localStorage.setItem('bragabjj_open_waiver', 'true');
              routedKey = 'kiosk';
              // Dispatch a soft in-memory event in case Kiosk is already mounted and listening.
              try {
                window.dispatchEvent(new CustomEvent('openWaiverNow'));
              } catch (evErr) {
                // ignore dispatch errors
              }
            }

            // Special-case: when user taps the promo item, open the Cyber Monday (Black Friday) promo modal locally
            if (key === 'blackfriday') {
              try {
                setPromoType('blackfriday');
                setShowPromo(true);
              } catch (e) {
                console.warn('Could not open promo modal', e);
              }
              routedKey = 'blackfriday';
            }
            if (key === 'christmas') {
              try {
                setPromoType('christmas');
                setShowPromo(true);
              } catch (e) {
                console.warn('Could not open christmas promo modal', e);
              }
              routedKey = 'christmas';
            }
          } catch (e) {
            // localStorage may be unavailable in some restricted contexts
            console.warn('Could not set open-waiver flag in localStorage', e);
          }

          if (typeof onSelect === 'function') {
            if (routedKey !== 'blackfriday' && routedKey !== 'christmas') {
              console.log('MobileCoreMenu: Calling onSelect with key:', routedKey, '(original:', key, ')');
              onSelect(routedKey);
            } else {
              console.log('MobileCoreMenu: Opening promo modal locally for', routedKey);
            }
          } else {
            if (routedKey !== 'blackfriday') {
              console.warn('MobileCoreMenu: onSelect prop is not a function. Key was:', routedKey);
            } else {
              console.log('MobileCoreMenu: onSelect missing but promo modal opened locally');
            }
          }

          setOpen(false);
          setAnimating(false);
        }, 200);
    } else {
      console.warn('MobileCoreMenu: Could not find button element for key:', key);
      
      if (key === 'blackfriday') {
        try { setPromoType('blackfriday'); setShowPromo(true); } catch (e) { console.warn('Could not open Black Friday promo modal', e); }
      } else if (key === 'christmas') {
        try { setPromoType('christmas'); setShowPromo(true); } catch (e) { console.warn('Could not open Christmas promo modal', e); }
      } else {
        if (typeof onSelect === 'function') {
          console.log('MobileCoreMenu: Calling onSelect directly with key:', key);
          onSelect(key);
        } else {
          console.warn('MobileCoreMenu: onSelect prop is not a function. Key was:', key);
        }
      }
      setOpen(false);
      setAnimating(false);
    }
  }, [onSelect]);

  


  // Add keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (open) {
        if (e.key === 'Escape') {
          setOpen(false);
          setAnimating(false);
        }
        if (e.key >= '1' && e.key <= '4') {
          const index = parseInt(e.key) - 1;
          if (menuItems[index]) {
            handleMenuSelect(menuItems[index].key);
          }
        }
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [open, handleMenuSelect]);

  // Enhanced wallet state management with initialization and account listening
  useEffect(() => {
    console.log('Setting up wallet event listeners...');
    
    // Initialize wallet state from localStorage
    initializeWallet();
    
    // Auto-connect on page load
    autoConnectWallet();

    if (isMetaMaskAvailable()) {
      // Enhanced account change handling with persistence
      const handleAccountsChanged = (accounts) => {
        console.log('Accounts changed:', accounts);
        if (accounts.length === 0) {
          console.log('All accounts disconnected');
          setWalletConnected(false);
          if (!walletAddress) setWalletAddressState(null);
          if (propIsAdmin === undefined) setIsAdmin(false);
          setShowWelcome(false);
          persistWalletState(null, false);
        } else {
          const address = accounts[0];
          console.log('Active account changed to:', address);
          setWalletConnected(true);
          if (!walletAddress) setWalletAddressState(address);
          
          const adminStatus = checkAdminStatus(address);
          if (propIsAdmin === undefined) setIsAdmin(adminStatus);
          persistWalletState(address, adminStatus);
          
          if (adminStatus) {
            setShowWelcome(true);
            setTimeout(() => setShowWelcome(false), 4000);
          }
        }
      };

      // Listen for chain changes
      const handleChainChanged = (chainId) => {
        console.log('Chain changed to:', chainId);
        // Reload the page when chain changes to ensure compatibility
        window.location.reload();
      };

      // Listen for connect/disconnect events
      const handleConnect = (connectInfo) => {
        console.log('Wallet connected:', connectInfo);
        checkWalletConnection();
      };

      const handleDisconnect = (error) => {
        console.log('Wallet disconnected:', error);
        setWalletConnected(false);
        if (!walletAddress) setWalletAddressState(null);
        if (propIsAdmin === undefined) setIsAdmin(false);
        setShowWelcome(false);
        persistWalletState(null, false);
      };

      // Add event listeners
      window.ethereum.on('accountsChanged', handleAccountsChanged);
      window.ethereum.on('chainChanged', handleChainChanged);
      window.ethereum.on('connect', handleConnect);
      window.ethereum.on('disconnect', handleDisconnect);

      // Cleanup listeners
      return () => {
        console.log('Cleaning up wallet event listeners...');
        if (window.ethereum.removeListener) {
          window.ethereum.removeListener('accountsChanged', handleAccountsChanged);
          window.ethereum.removeListener('chainChanged', handleChainChanged);
          window.ethereum.removeListener('connect', handleConnect);
          window.ethereum.removeListener('disconnect', handleDisconnect);
        }
      };
    } else {
      console.log('MetaMask not available, skipping event listeners');
    }
  }, [initializeWallet, autoConnectWallet, isMetaMaskAvailable, checkAdminStatus, persistWalletState, propIsAdmin, walletAddress, checkWalletConnection]);

  // ✅ Secondary admin revalidation: handles hydration/timing edge cases
  useEffect(() => {
    if (walletConnected && currentWalletAddress && !currentIsAdmin) {
      if (checkAdminStatus(currentWalletAddress)) {
        console.log('Admin revalidated post-connect (secondary check).');
        setIsAdmin(true);
      }
    }
  }, [walletConnected, currentWalletAddress, currentIsAdmin, checkAdminStatus]);

  // Debug overlay for orb/admin/wallet state
  // Made opt-in: enable by running in browser console:
  // localStorage.setItem('bragabjj_debug','true')
  const debugFlag = (typeof window !== 'undefined') && localStorage.getItem('bragabjj_debug') === 'true';
  let isSmallScreen = false;
  try {
    isSmallScreen = (typeof window !== 'undefined') && window.innerWidth <= 768;
  } catch (e) {
    console.warn('📱 Error checking isSmallScreen:', e);
    isSmallScreen = false;
  }
  const debug = debugFlag;

  // Gather debug info
  const debugInfo = {
    open,
    showHorizontalMenu,
    orbVisible: !showHorizontalMenu,
    walletConnected,
    walletAddress: currentWalletAddress,
    isAdmin: currentIsAdmin,
    propIsAdmin,
    adminWallets: ADMIN_WALLETS,
    localStorageWallet: localStorage.getItem('bragabjj_wallet_address'),
    localStorageAdmin: localStorage.getItem('bragabjj_admin_status'),
    walletBrowser: walletBrowserInfo,
    dataSyncStatus: dataSync.status,
    quickActions,
    showWelcome,
    showConnectPopup,
    showWalletModal,
    autoConnectAttempted,
    isConnecting,
    realTimeDataSummary: {
      students: realTimeData?.students?.length,
      leads: realTimeData?.leads?.length,
      todayAttendance: realTimeData?.todayAttendance,
      monthlyRevenue: realTimeData?.monthlyRevenue
    },
    // ✅ extra line to help confirm admin match
    detectedAdmin: checkAdminStatus(currentWalletAddress),
  };

  // Log key state when admin/wallet state changes to aid debugging
  useEffect(() => {
    try {
      console.log('MobileCoreMenu State:', {
        currentIsAdmin,
        showHorizontalMenu,
        walletConnected,
        currentWalletAddress,
        debugFlag,
        isSmallScreen
      });
    } catch (e) {
      console.log('Error logging MobileCoreMenu state', e);
    }
  }, [currentIsAdmin, showHorizontalMenu, walletConnected, currentWalletAddress, debugFlag, isSmallScreen]);

  // Safety: if horizontal menu is open but user is no longer admin, close it
  useEffect(() => {
    if (showHorizontalMenu && !currentIsAdmin) {
      console.warn('Horizontal menu was open but currentIsAdmin is false — closing it');
      setShowHorizontalMenu(false);
    }
  }, [showHorizontalMenu, currentIsAdmin]);

  // When admin status or wallet connection changes, reset menu states to avoid blank UI
  useEffect(() => {
    // If admin status changes, ensure horizontal menu is closed and orb is visible
    setShowHorizontalMenu(false);
    setOpen(false);
  }, [currentIsAdmin]);

  // If wallet disconnects unexpectedly, reset admin and menus
  useEffect(() => {
    if (!walletConnected) {
      setShowHorizontalMenu(false);
      setOpen(false);
    }
  }, [walletConnected]);

  // Immediate fallback: detect if the orb element is not visible (e.g. hidden by CSS/overlay)
  useEffect(() => {
    const isElementVisible = (el) => {
      if (!el) return false;
      try {
        const rects = el.getClientRects();
        if (!rects || rects.length === 0) return false;
        const rect = el.getBoundingClientRect();
        const style = window.getComputedStyle(el);
        if (style.display === 'none' || style.visibility === 'hidden' || parseFloat(style.opacity) === 0) return false;
        if (rect.width < 6 || rect.height < 6) return false;

        // Check if the orb's center point is on top (not covered by another element)
        const cx = rect.left + rect.width / 2;
        const cy = rect.top + rect.height / 2;
        try {
          if (typeof window !== 'undefined' && (cx < 0 || cy < 0 || cx > window.innerWidth || cy > window.innerHeight)) return false;
        } catch (e) {
          console.warn('📱 Error checking orb position:', e);
          return false;
        }
        const topEl = document.elementFromPoint(cx, cy);
        if (!topEl) return false;
        // If the orb element is not the returned element or an ancestor, it may be covered
        if (!el.contains(topEl) && !topEl.contains(el)) {
          return false;
        }

        return true;
      } catch (e) {
        return false;
      }
    };

    const check = () => {
      try {
        const el = orbRef.current;
        const visible = isElementVisible(el);
        // Show fallback if orb isn't visible and menu is not already open and horizontal menu not open
        setFallbackVisible((prev) => {
          const should = (!visible && !open && !showHorizontalMenu);
          return should;
        });
      } catch (e) {
        setFallbackVisible(true);
      }
    };

    // Run immediately and a few times to catch async rendering/animations
    check();
    const t1 = setTimeout(check, 150);
    const t2 = setTimeout(check, 450);

    // Also check periodically for short duration to catch overlays
    let intervalId = setInterval(check, 600);
    const intervalTimeout = setTimeout(() => {
      clearInterval(intervalId);
      intervalId = null;
    }, 4000);

    window.addEventListener('resize', check);
    window.addEventListener('scroll', check, { passive: true });

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(intervalTimeout);
      if (intervalId) clearInterval(intervalId);
      window.removeEventListener('resize', check);
      window.removeEventListener('scroll', check);
    };
  }, [open, showHorizontalMenu, currentIsAdmin]);

  // When fallback becomes visible for admin, fetch stats
  useEffect(() => {
    if (fallbackVisible && currentIsAdmin) {
      fetchFallbackStats();
    }
  }, [fallbackVisible, currentIsAdmin, fetchFallbackStats]);

  // Keep fallback stats updated while admin is active and the fallback is visible or minimized
  useEffect(() => {
    if (!currentIsAdmin) return undefined;

    // Only poll while admin quick access is relevant (visible or minimized)
    if (!fallbackVisible && !fallbackMinimized) return undefined;

    // immediate fetch
    fetchFallbackStats();

    const pollInterval = 30000; // 30s
    const id = setInterval(() => {
      fetchFallbackStats();
    }, pollInterval);

    return () => clearInterval(id);
  }, [currentIsAdmin, fallbackVisible, fallbackMinimized, fetchFallbackStats]);

  // Ensure we have stats for admin when the component mounts or when realtime data isn't provided.
  // This will fetch fallback stats from the API if `realTimeData` is missing or incomplete.
  useEffect(() => {
    const shouldFetchFallback = currentIsAdmin && (
      !realTimeData || !Array.isArray(realTimeData.students) || realTimeData.students.length === 0
    );

    if (shouldFetchFallback) {
      // best-effort fetch, don't block UI
      fetchFallbackStats().catch(err => console.warn('fallback stats fetch failed', err));
    }
  }, [currentIsAdmin, realTimeData, fetchFallbackStats]);

  // Error boundary wrapper for render
  try {
    console.log('📱 MobileCoreMenu: Rendering JSX...');
    
  return (
    <div className={`mobile-menu-root ${open ? 'menu-open' : ''}`}>
      {debug && (
        // Non-interactive overlay so it doesn't block orb clicks on mobile
        <div style={{position:'fixed',top:8,right:8,zIndex:999,background:'#222cc',color:'#fff',padding:10,fontSize:12,maxWidth:360,wordBreak:'break-all',borderRadius:8,boxShadow:'0 2px 12px #0008',pointerEvents:'none',opacity: isSmallScreen ? 0.9 : 1}}>
          <b>DEBUG OVERLAY</b><br/>
          {Object.entries(debugInfo).map(([k,v]) => (
            <div key={k}><b>{k}:</b> {typeof v === 'object' ? JSON.stringify(v) : String(v)}</div>
          ))}
        </div>
      )}
  {/* Wallet Browser Indicator */}
      {walletBrowserInfo.inBrowser && (
        <div style={{
          position: 'fixed',
          top: '10px',
          left: '50%',
          transform: 'translateX(-50%)',
          background: 'rgba(20, 241, 149, 0.1)',
          border: '1px solid rgba(20, 241, 149, 0.3)',
          borderRadius: '20px',
          padding: '6px 12px',
          fontSize: '12px',
          color: '#14f195',
          zIndex: 1001,
          backdropFilter: 'blur(10px)',
          display: 'flex',
          alignItems: 'center',
          gap: '6px'
        }}>
          <span>🌐</span>
          <span>Running in {walletBrowserInfo.name} Browser</span>
          <span>✅</span>
        </div>
      )}

      {/* Enhanced Horizontal Top Menu for Mobile Admin */}
      {showHorizontalMenu && currentIsAdmin && (
        <div className="horizontal-menu-overlay" onClick={() => setShowHorizontalMenu(false)}>
          <div className="horizontal-menu-container" onClick={(e) => e.stopPropagation()}>
            {/* Enhanced BragaBJJ Logo Header with Status */}
            <div className="horizontal-menu-header">
              <div className="logo-section">
                <div className="logo-icon">🥋</div>
                <div className="logo-text">
                  <h1>Outbox BJJ</h1>
                  <span>Admin Dashboard</span>
                </div>
                {/* Admin Status Indicators */}
                <div className="admin-status-mini">
                  <div className={`sync-indicator ${dataSync.status}`} title={`Last sync: ${dataSync.lastSync?.toLocaleTimeString() || 'Never'}`}>
                    {dataSync.status === 'syncing' ? '🔄' : dataSync.status === 'success' ? '✅' : dataSync.status === 'error' ? '❌' : '⏸️'}
                  </div>
                  {quickActions.messageCount > 0 && (
                    <div className="message-count-badge" title={`${quickActions.messageCount} unread messages`}>
                      💬 {quickActions.messageCount}
                    </div>
                  )}
                  {quickActions.systemAlerts > 0 && (
                    <div className="alert-count-badge" title={`${quickActions.systemAlerts} system alerts`}>
                      ⚠️ {quickActions.systemAlerts}
                    </div>
                  )}
                </div>
              </div>
              <div className="header-actions">
                {/* Quick Admin Actions */}
                <button 
                  className="quick-action sync-btn"
                  onClick={() => handleDataSync(true)}
                  disabled={dataSync.status === 'syncing'}
                  title="Force data synchronization"
                >
                  {dataSync.status === 'syncing' ? '🔄' : '🔄'}
                </button>
                <button 
                  className="quick-action message-btn"
                  onClick={() => handleQuickAction('broadcast_message', { recipients: realTimeData?.students?.length || 0 })}
                  title="Send broadcast message"
                >
                  📢
                </button>
                <button 
                  className="close-horizontal-menu"
                  onClick={() => setShowHorizontalMenu(false)}
                >
                  ✕
                </button>
              </div>
            </div>
            
            {/* Horizontal Tab Menu */}
            <div className="horizontal-tab-menu">
              {menuItems.map((item, index) => (
                <button
                  key={item.key}
                  className={`horizontal-tab ${item.key === currentView ? 'active' : ''} ${item.className}`}
                  onClick={() => {
                    handleMenuSelect(item.key);
                    setShowHorizontalMenu(false);
                  }}
                >
                  <span className="tab-icon">{item.icon}</span>
                  <div className="tab-content">
                    <span className="tab-label">{item.label}</span>
                    <span className="tab-description">{item.description}</span>
                  </div>
                  {currentIsAdmin && item.key === 'admin' && (
                    <div className="admin-crown-tab">👑</div>
                  )}
                </button>
              ))}
            </div>
            
            {/* Enhanced Data Summary Panel */}
            <div className="horizontal-data-summary">
              <div className="data-stats-row">
                <div className="stat-item">
                  <span className="stat-icon">👥</span>
                  <span className="stat-value">{(realTimeData?.students?.length ?? fallbackStats.students ?? 0)}</span>
                  <span className="stat-label">Students</span>
                </div>
                <div className="stat-item">
                  <span className="stat-icon">✅</span>
                  <span className="stat-value">{(realTimeData?.todayAttendance ?? fallbackStats.checkedToday ?? 0)}</span>
                  <span className="stat-label">Today</span>
                </div>
                <div className="stat-item">
                  <span className="stat-icon">🎯</span>
                  <span className="stat-value">{(realTimeData?.leads?.length ?? fallbackStats.leads ?? 0)}</span>
                  <span className="stat-label">Leads</span>
                </div>
                <div className="stat-item">
                  <span className="stat-icon">💰</span>
                  <span className="stat-value">{`$${( (typeof realTimeData?.monthlyRevenue === 'number') ? realTimeData.monthlyRevenue : ((realTimeData?.students?.length ?? fallbackStats.students ?? 0) * 129) ).toLocaleString()}`}</span>
                  <span className="stat-label">Revenue</span>
                </div>
              </div>
            </div>

            {/* Enhanced Wallet Status in Horizontal Menu */}
            <div className="horizontal-wallet-status">
              <div className="wallet-info-horizontal">
                <span className="wallet-emoji">👛</span>
                <div className="wallet-details">
                  <span className="wallet-status">
                    {walletConnected ? 'Connected' : 'Not Connected'}
                  </span>
                  {walletConnected && (
                    <span className="wallet-address-horizontal">
                      {currentWalletAddress?.slice(0, 6)}...{currentWalletAddress?.slice(-4)}
                    </span>
                  )}
                </div>
                {currentIsAdmin && (
                  <div className="admin-badge-horizontal-enhanced">
                    <span className="admin-crown">👑</span>
                    <span className="admin-text">SUPREME ADMIN</span>
                    <div className="admin-privilege-indicators">
                      <span className="privilege-dot active" title="Data Access">●</span>
                      <span className="privilege-dot active" title="Communications">●</span>
                      <span className="privilege-dot active" title="Analytics">●</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Glassmorphic Orb: Only show for non-admin, or for admin when horizontal menu is NOT open */}
      {(!currentIsAdmin && !showHorizontalMenu) && (
        <div
          ref={orbRef}
          className={`mobile-menu-orb ${open ? 'menu-open' : ''}`}
          onClick={handleOrbClick}
          style={{ zIndex: (animating || open) ? 10030 : 1003, pointerEvents: 'auto', transform: (animating ? 'translateY(-6px)' : undefined), transition: 'transform 260ms ease' }}
        >
          <div className="orb-glow-ring"></div>
          <span className="mobile-menu-logo">01</span>
          <span className="mobile-menu-title">Outbox BJJ</span>
        </div>
      )}
      {/* Admin: Golden orb with crown, only if horizontal menu is NOT open */}
      {(currentIsAdmin && !showHorizontalMenu) && (
        <div
          ref={orbRef}
          className={`mobile-menu-orb admin-orb golden-mode ${open ? 'menu-open' : ''}`}
          onClick={handleOrbClick}
          style={{
            background: 'linear-gradient(135deg, #ffd700 60%, #fffbe6 100%)',
            boxShadow: (animating || open) ? '0 0 48px 12px #ffd700cc, 0 0 0 6px #fffbe6cc' : '0 0 32px 8px #ffd70099, 0 0 0 6px #fffbe6cc',
            border: '2.5px solid #ffd700',
            position: 'relative',
            zIndex: (animating || open) ? 10030 : 1003,
            pointerEvents: 'auto',
            transition: 'box-shadow 260ms ease, transform 260ms ease',
            transform: (animating ? 'translateY(-6px)' : undefined)
          }}
        >
          {/* Crown on top for admin */}
          <div style={{
            position: 'absolute',
            top: '-32px',
            left: '50%',
            transform: 'translateX(-50%)',
            fontSize: '2.2rem',
            filter: 'drop-shadow(0 2px 8px #ffd700cc)',
            zIndex: 2,
            animation: 'crownFloat 2.2s infinite ease-in-out',
            pointerEvents: 'none',
          }}>👑</div>
          <div className="orb-glow-ring"></div>
          {/* decorative seasonal hat removed */}
          <span className="mobile-menu-logo" style={{ color: '#ffd700', textShadow: '0 0 12px #ffd700cc' }}>B</span>
          <span className="mobile-menu-title" style={{ color: '#ffd700', textShadow: '0 0 8px #ffd700cc' }}>BragaBJJ</span>
          {/* Status Indicators */}
          <div className="orb-status-indicators">
            {dataSync.status === 'syncing' && (
              <div className="status-indicator sync-indicator">🔄</div>
            )}
            {quickActions.messageCount > 0 && (
              <div className="status-indicator message-indicator">
                💬<span className="indicator-count">{quickActions.messageCount}</span>
              </div>
            )}
            {quickActions.systemAlerts > 0 && (
              <div className="status-indicator alert-indicator">
                ⚠️<span className="indicator-count">{quickActions.systemAlerts}</span>
              </div>
            )}
            {dataSync.status === 'error' && (
              <div className="status-indicator error-indicator">❌</div>
            )}
            {dataSync.status === 'success' && (
              <div className="status-indicator success-indicator">✅</div>
            )}
          </div>
        </div>
      )}

      {/* Enhanced Notification System */}
      {notifications.length > 0 && (
        <div className="notification-container">
          {notifications.map(notification => (
            <div 
              key={notification.id}
              className={`notification notification-${notification.type}`}
              onClick={() => removeNotification(notification.id)}
            >
              <div className="notification-header">
                <span className="notification-icon">
                  {notification.type === 'success' ? '✅' : 
                   notification.type === 'error' ? '❌' : 
                   notification.type === 'warning' ? '⚠️' : 'ℹ️'}
                </span>
                <span className="notification-title">{notification.title}</span>
                <button className="notification-close">×</button>
              </div>
              <div className="notification-message">{notification.message}</div>
            </div>
          ))}
        </div>
      )}
      
      {/* Admin Welcome Message */}
      {showWelcome && currentIsAdmin && (
        <div className="admin-welcome">
          <div className="welcome-content">
            <div className="welcome-icon">👑</div>
            <div className="welcome-text">
              <h3>Welcome Admin</h3>
              <p>Full access granted</p>
            </div>
          </div>
        </div>
      )}
      
      {open && (
        <div className={`mobile-menu-list ${animating ? 'opening' : ''}`} style={{ zIndex: 1004 }}>
          {menuItems.map((item, index) => (
            <button 
              key={item.key} 
              type="button"
              data-key={item.key}
              className={`mobile-menu-btn ${item.className} ${currentIsAdmin && item.key === 'admin' ? 'admin-highlighted' : ''}`}
              onClick={(e) => { e.stopPropagation(); handleMenuSelect(item.key); }}
              style={{ '--delay': `${index * 0.1}s` }}
            >
              <span className="mobile-menu-icon">{item.icon}</span>
              <div style={{ flex: 1, textAlign: 'left' }}>
                <span className="mobile-menu-label">{item.label}</span>
                <div style={{ 
                  fontSize: '12px', 
                  opacity: 0.7, 
                  marginTop: '2px',
                  color: 'rgba(255, 255, 255, 0.6)'
                }}>
                  {item.description}
                  {currentIsAdmin && item.key === 'admin' && <span style={{ color: '#14f195', marginLeft: '8px' }}>✓</span>}
                </div>
              </div>
              <span style={{ 
                fontSize: '12px', 
                opacity: 0.5,
                color: 'rgba(255, 255, 255, 0.4)'
              }}>
                {index + 1}
              </span>
            </button>
          ))}
        </div>
      )}
      
      {/* Backdrop overlay for better UX */}
      {open && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.2)',
            backdropFilter: 'blur(2px)',
            zIndex: 1002
          }}
          onClick={() => {
            setOpen(false);
            setAnimating(false);
          }}
        />
      )}

      {/* Wallet Connector - Lower Middle Section */}
      <div className="wallet-connector-container">
        <div 
          className={`wallet-connector ${walletConnected ? 'connected' : 'disconnected'} ${isConnecting ? 'connecting' : ''} ${currentIsAdmin ? 'admin-wallet' : ''}`}
          onClick={(e) => handleWalletConnectorClick(e)}
        >
          <div className="wallet-connector-glow"></div>
          <div className="wallet-connector-content">
            {isConnecting ? (
              <>
                <div className="wallet-spinner">⟳</div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                  <span className="wallet-text">Connecting...</span>
                  <span style={{ fontSize: '11px', opacity: 0.7, color: 'rgba(255, 255, 255, 0.6)' }}>
                    Check your wallet browser
                  </span>
                </div>
              </>
            ) : walletConnected ? (
              <>
                <span className="wallet-emoji">👛</span>
                <div className="wallet-info-compact">
                  <span className="wallet-status">Connected & Confirmed</span>
                  <span className="wallet-address-short">
                    {currentWalletAddress?.slice(0, 6)}...{currentWalletAddress?.slice(-4)}
                  </span>
                  {currentIsAdmin && <span className="admin-badge">ADMIN</span>}
                  {localStorage.getItem('bragabjj_wallet_confirmed') === 'true' && (
                    <span style={{ fontSize: '10px', color: '#14f195', fontWeight: 'bold' }}>✓ VERIFIED</span>
                  )}
                </div>
              </>
            ) : (
              <>
                <span className="wallet-emoji">👛</span>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                  <span className="wallet-text">Connect Wallet</span>
                  <span style={{ fontSize: '11px', opacity: 0.7, color: 'rgba(255, 255, 255, 0.6)' }}>
                    Tap to open wallet browser
                  </span>
                </div>
              </>
            )}
          </div>
          <div className={`wallet-status-dot ${walletConnected ? 'connected' : 'disconnected'} ${currentIsAdmin ? 'admin' : ''}`}></div>
        </div>
      </div>

      {/* Immediate fallback button: appears when orb seems hidden to ensure menu remains accessible */}
      {fallbackVisible && (
        currentIsAdmin ? (
          // Admin fallback panel with golden/neon details
          <div
            className="mobile-menu-fallback-admin"
            style={{
              position: 'fixed',
              right: 12,
              bottom: 84,
              zIndex: 10010,
              width: 300,
              maxWidth: '80%',
              padding: 12,
              borderRadius: 14,
              background: 'linear-gradient(135deg, rgba(255,244,200,0.95), rgba(255,238,150,0.92))',
              boxShadow: '0 12px 30px rgba(0,0,0,0.35), 0 0 24px rgba(255,215,0,0.12) inset',
              color: '#111',
              border: '1px solid rgba(255,215,0,0.6)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ fontSize: 22, filter: 'drop-shadow(0 2px 8px #ffd70066)' }}>👑</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: '700', color: '#2b2b2b' }}>Admin Quick Menu</div>
                <div style={{ fontSize: 12, color: '#555' }}>Fallback — stats & quick actions</div>
              </div>
              <button
                onClick={() => {
                  // close (minimize) fallback manually
                  setFallbackVisible(false);
                  setFallbackMinimized(true);
                }}
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 14 }}
                title="Dismiss"
              >
                ✕
              </button>
            </div>

            <StatsGrid
              fallbackStats={fallbackStats}
              realTimeData={realTimeData}
              onRefresh={fetchFallbackStats}
              onStatClick={(statKey) => {
                const mapping = {
                  students: 'student',
                  leads: 'leads',
                  attendance: 'admin',
                  orders: 'admin'
                };
                const target = mapping[statKey] || 'admin';
                // For small screens open the horizontal admin menu, otherwise route to the admin view
                if ((typeof window !== 'undefined') && window.innerWidth <= 768) setShowHorizontalMenu(true);
                handleMenuSelect(target);
                setFallbackVisible(false);
                setFallbackMinimized(true);
              }}
            />

            <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
              <button
                onClick={() => {
                  // open horizontal admin menu if small or full admin otherwise
                  if ((typeof window !== 'undefined') && window.innerWidth <= 768) setShowHorizontalMenu(true);
                  else handleMenuSelect('admin');
                  // minimize fallback after opening admin
                  setFallbackVisible(false);
                  setFallbackMinimized(true);
                }}
                style={{ flex: 1, padding: '8px 10px', borderRadius: 8, border: 'none', cursor: 'pointer', background: 'linear-gradient(90deg,#ffd54a,#ffeb3b)', boxShadow: '0 6px 18px rgba(255,215,0,0.12)' }}
              >
                Open Admin
              </button>
              <button
                onClick={() => fetchFallbackStats(true)}
                style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid rgba(255,215,0,0.2)', background: 'transparent', cursor: 'pointer' }}
              >f
                Refresh
              </button>
            </div>

            <div style={{ marginTop: 8, fontSize: 11, color: '#444' }}>
              <span style={{ color: '#0fffd9', textShadow: '0 0 8px rgba(20,241,149,0.6)' }}>Neon</span> details enabled
            </div>
          </div>
        ) : (
          <button
            className="mobile-menu-fallback-button"
            onClick={(e) => {
              e.stopPropagation();
              // Admins on small screens prefer horizontal menu
              if (currentIsAdmin && (typeof window !== 'undefined') && window.innerWidth <= 768) {
                setShowHorizontalMenu(true);
              } else {
                setOpen(true);
                setAnimating(true);
              }
              // Hide fallback once used (menu will appear)
              setFallbackVisible(false);
            }}
            style={{
              position: 'fixed',
              right: 16,
              bottom: 80,
              zIndex: 10010,
              background: '#14f195',
              color: '#001',
              borderRadius: 28,
              padding: '8px 12px',
              boxShadow: '0 8px 24px rgba(20,241,149,0.18)',
              border: 'none',
              fontSize: 13,
              cursor: 'pointer'
            }}
            title="Open Menu"
          >
            {currentIsAdmin ? 'Admin ☰' : 'Menu ☰'}
          </button>
        )
      )}

      {/* Minimized fallback pill (glowing gold) shown when admin minimized the fallback */}
      {fallbackMinimized && !fallbackVisible && currentIsAdmin && (
        <button
          className="mobile-menu-fallback-minimized"
          onClick={(e) => { e.stopPropagation(); setFallbackVisible(true); setFallbackMinimized(false); }}
          style={{
            position: 'fixed',
            right: 12,
            bottom: 84,
            zIndex: 10020,
            width: 56,
            height: 56,
            borderRadius: 28,
            background: 'linear-gradient(135deg,#fff8e1,#fff1a8)',
            border: '2px solid #ffd700',
            boxShadow: '0 8px 28px rgba(255,215,0,0.18), 0 0 12px rgba(255,215,0,0.12) inset',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            color: '#222',
            fontWeight: 700
          }}
          title="Open Admin Quick Menu"
        >
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1 }}>
            <div style={{ fontSize: 18, filter: 'drop-shadow(0 2px 6px #ffd70066)' }}>👑</div>
            <div style={{ fontSize: 10, marginTop: 0, color: '#333' }}>{fallbackStats.students}</div>
          </div>
        </button>
      )}

      {/* Connect Popup: shows a welcome and offers to open MetaMask mobile browser */}
      {showConnectPopup && (
        <div className="connect-popup" onClick={(e) => e.stopPropagation()}>
          <div className="connect-popup-content">
            <h4>🎉 Wallet Connected Successfully!</h4>
            <p>Welcome to OutboxBJJ! Your wallet is now connected and confirmed.</p>
            <div style={{ 
              background: 'rgba(20, 241, 149, 0.1)', 
              border: '1px solid rgba(20, 241, 149, 0.3)',
              borderRadius: '12px',
              padding: '12px',
              margin: '12px 0',
              fontSize: '14px'
            }}>
              <strong>💡 Pro Tip:</strong> For the best experience on mobile, use this dapp directly in your MetaMask browser by tapping "Browser" in the MetaMask app.
            </div>
            <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
              <button
                type="button"
                className="wallet-btn primary"
                onClick={() => {
                  // Open MetaMask mobile deep link to this dapp
                  try {
                    const currentPath = window.location.pathname + window.location.search;
                    const mmUrl = `https://metamask.app.link/dapp/${window.location.host}${currentPath}`;
                    
                    // Copy URL to clipboard as backup
                    navigator.clipboard.writeText(window.location.href).catch(() => {});
                    
                    window.open(mmUrl, '_blank');
                  } catch (err) {
                    // Fallback to MetaMask download
                    window.open('https://metamask.io/download/', '_blank');
                  }
                }}
              >
                🔗 Open in MetaMask Browser
              </button>
              <button type="button" className="wallet-btn secondary" onClick={() => setShowConnectPopup(false)}>
                Continue Here
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Wallet Connection Modal */}
      {showWalletModal && (
        <div className="wallet-modal-overlay" onClick={() => setShowWalletModal(false)}>
          <div className="wallet-modal" onClick={(e) => e.stopPropagation()}>
            <div className="wallet-modal-header">
              <h3>Wallet Status</h3>
              <button 
                className="wallet-modal-close"
                onClick={() => setShowWalletModal(false)}
              >
                ✕
              </button>
            </div>
            <div className="wallet-modal-content">
              {walletConnected ? (
                <>
                  <div className="wallet-info">
                    <div className="wallet-status-indicator connected">●</div>
                    <span>Connected</span>
                  </div>
                  <div className="wallet-address">
                    {currentWalletAddress?.slice(0, 8)}...{currentWalletAddress?.slice(-8)}
                  </div>
                  <div className="wallet-actions">
                    <button 
                      className="wallet-btn secondary"
                      onClick={() => navigator.clipboard.writeText(currentWalletAddress)}
                    >
                      Copy Address
                    </button>
                    <button 
                      className="wallet-btn primary"
                      onClick={disconnectWallet}
                    >
                      Disconnect
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="wallet-info">
                    <div className="wallet-status-indicator disconnected">●</div>
                    <span>Not Connected</span>
                  </div>
                  <p>Connect your wallet to access student features and manage your account.</p>
                  <div className="wallet-actions">
                    <button 
                      className="wallet-btn primary"
                      onClick={connectWallet}
                      disabled={isConnecting}
                    >
                      {isConnecting ? 'Connecting...' : 'Connect Wallet'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Black Friday Promo Modal (lazy-loaded) */}
      {showPromo && (
        <Suspense fallback={<div className="promo-loading" style={{position:'fixed',inset:0,display:'flex',alignItems:'center',justifyContent:'center',zIndex:11000}}>Loading…</div>}>
          <div className="mobile-promo-overlay" style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.45)',zIndex:11000,display:'flex',alignItems:'center',justifyContent:'center'}} onClick={() => { setShowPromo(false); setPromoType(null); }}>
            <div style={{width:'min(920px,96%)', maxHeight:'96%', overflow:'auto'}} onClick={(e) => e.stopPropagation()}>
              {promoType === 'christmas' ? (
                <Christmas onClose={() => { setShowPromo(false); setPromoType(null); }} adminWallet={currentWalletAddress} />
              ) : (
                <MatrixPromo onClose={() => { setShowPromo(false); setPromoType(null); }} adminWallet={currentWalletAddress} />
              )}
            </div>
          </div>
        </Suspense>
      )}
    </div>
  );
  } catch (error) {
    console.error('📱 MobileCoreMenu: Render error:', error);
    return (
      <div style={{
        width: '100%',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        background: 'radial-gradient(circle at 30% 30%, #0f172a, #1e293b)',
        color: 'white'
      }}>
        <h2 style={{ fontSize: '24px', marginBottom: '16px' }}>Mobile Menu Error</h2>
        <p style={{ marginBottom: '16px', textAlign: 'center', maxWidth: '400px' }}>
          Failed to load mobile menu. This may be due to browser compatibility issues.
        </p>
        <button 
          onClick={() => window.location.reload()}
          style={{
            padding: '12px 24px',
            background: '#00fff7',
            color: '#000',
            border: 'none',
            borderRadius: '8px',
            fontSize: '16px',
            fontWeight: 'bold',
            cursor: 'pointer'
          }}
        >
          Reload Page
        </button>
        {error && (
          <pre style={{
            marginTop: '20px',
            fontSize: '11px',
            opacity: 0.7,
            maxWidth: '90vw',
            overflow: 'auto',
            padding: '12px',
            background: 'rgba(0,0,0,0.3)',
            borderRadius: '8px'
          }}>
            {error.toString()}
          </pre>
        )}
      </div>
    );
  }
}
